import React, { useEffect, useState } from 'react';
import { supabase } from '../supabase';
import { SidebarLayout } from '../components/Sidebar';
import CruxInsight from '../components/CruxInsight';
import Card from '../design-system/Card';
import LoadingState from '../components/LoadingState';
import { colors, radius, spacing, fontStack } from '../design-system/tokens';

/* Stub data — used for users with at least one signal logged. The fresh-user
   case is handled by NEW_USER_INSIGHT below. Cross-module Sonnet insight is
   future polish; both paths feed CruxInsight directly. */
const STUB_INSIGHT = {
  state:
    'You are slightly fatigued but focused. Energy reserves are below average, and your skill momentum is strong.',
  actions: [
    'One deep work session this morning',
    'Avoid scheduling meetings after 3pm',
    'Light movement before dinner',
  ],
  reasons: [
    'Recovery score trending down 3 days',
    'Growth metrics up 12% this week',
    'Calendar load is elevated',
  ],
  confidence: 84,
};

const NEW_USER_INSIGHT = {
  state:
    'Curx is waiting for your first signals. Log an energy check-in or a few skills to unlock real insight.',
  actions: [
    'Log your first energy check-in',
    'Add 3-5 skills you care about in Growth',
    'Try one question in Copilot to see the format',
  ],
  reasons: [
    'Insights need at least one log to ground in',
    'A few days of data unlocks the trend chart',
  ],
  confidence: 50,
};

const MODULES = [
  {
    path: '/energy',
    name: 'Energy',
    subtitle: 'Yesterday in 5 seconds',
    metric: null,
    suggestion: 'Log yesterday and Curx will explain it.',
    accent: colors.accentEnergy,
    Icon: BoltIcon,
  },
  {
    path: '/growth',
    name: 'Growth',
    subtitle: 'Skills & momentum',
    metric: 'Momentum: +12%',
    suggestion: 'Practice a system-design prompt today.',
    accent: colors.accentGrowth,
    Icon: SeedlingIcon,
  },
  {
    path: '/system',
    name: 'System',
    subtitle: 'Health, money & plan',
    metric: 'On track: 7/10',
    suggestion: 'Review the budget category running hot.',
    accent: colors.accentSystem,
    Icon: GridIcon,
  },
];

export default function Home({ user, onSignOut }) {
  const [firstName, setFirstName] = useState('');
  // 'unknown' until we've checked Supabase for at least one signal.
  // Once we know, we either render NEW_USER_INSIGHT or STUB_INSIGHT.
  const [signalState, setSignalState] = useState('unknown'); // 'unknown' | 'fresh' | 'returning'

  useEffect(() => {
    if (user) {
      const name =
        user?.user_metadata?.full_name?.split(' ')[0] ||
        user?.email?.split('@')[0] ||
        '';
      setFirstName(name);
    } else {
      supabase.auth.getUser().then(({ data }) => {
        const u = data?.user;
        if (!u) return;
        const name =
          u.user_metadata?.full_name?.split(' ')[0] ||
          u.email?.split('@')[0] ||
          '';
        setFirstName(name);
      });
    }
  }, [user]);

  // Lightweight "do they have any signal yet?" check. We don't render full
  // module data here — Phase 1 keeps Home stub-driven — but we do want to
  // distinguish a brand-new user from a returning one so the insight copy
  // doesn't lie ("you are slightly fatigued" before any check-in).
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data: u } = await supabase.auth.getUser();
        const uid = u?.user?.id;
        if (!uid) {
          if (!cancelled) setSignalState('fresh');
          return;
        }
        // Probe each table with HEAD-style limit-1 selects in parallel.
        const probes = await Promise.allSettled([
          supabase.from('energy_logs').select('id').eq('user_id', uid).limit(1),
          supabase.from('skill_scores').select('user_id').eq('user_id', uid).limit(1),
          supabase.from('planner_tasks').select('id').eq('user_id', uid).limit(1),
        ]);
        const anyData = probes.some(
          (r) => r.status === 'fulfilled' && Array.isArray(r.value?.data) && r.value.data.length > 0
        );
        if (!cancelled) setSignalState(anyData ? 'returning' : 'fresh');
      } catch (_) {
        if (!cancelled) setSignalState('fresh');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [user]);

  const insight = signalState === 'returning' ? STUB_INSIGHT : NEW_USER_INSIGHT;
  const subtitle =
    signalState === 'unknown'
      ? 'Curx is reading your world…'
      : signalState === 'fresh'
      ? 'Welcome to Curx. Three modules. One decision engine.'
      : 'Curx is reading your world…';

  return (
    <SidebarLayout currentPath="/" user={user} onSignOut={onSignOut}>
      <div style={{ maxWidth: 1100, margin: '0 auto' }}>
        {/* Greeting */}
        <header style={{ marginBottom: spacing.xl, fontFamily: fontStack }}>
          <h1
            style={{
              margin: 0,
              fontSize: 28,
              fontWeight: 600,
              color: colors.textPrimary,
              letterSpacing: '-0.01em',
            }}
          >
            {greeting()}{firstName ? `, ${firstName}` : ''}
          </h1>
          <p
            style={{
              margin: '6px 0 0',
              fontSize: 15,
              color: colors.textSecondary,
            }}
          >
            {subtitle}
          </p>
        </header>

        {/* Curx Insight */}
        <div style={{ marginBottom: spacing.xl }}>
          {signalState === 'unknown' ? (
            <LoadingState rows={3} label="Loading insight" />
          ) : (
            <CruxInsight
              state={insight.state}
              actions={insight.actions}
              reasons={insight.reasons}
              confidence={insight.confidence}
            />
          )}
        </div>

        {/* Module summary cards */}
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))',
            gap: spacing.base,
            marginBottom: spacing.xl,
          }}
        >
          {MODULES.map((m) => (
            <ModuleCard key={m.path} module={m} />
          ))}
        </div>

        {/* Ask Curx CTA */}
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            padding: `${spacing.xl}px 0`,
          }}
        >
          <a
            href="/copilot"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 10,
              padding: '14px 32px',
              borderRadius: 999,
              backgroundColor: colors.primary,
              color: '#fff',
              fontFamily: fontStack,
              fontSize: 16,
              fontWeight: 600,
              textDecoration: 'none',
              boxShadow: '0 4px 12px rgba(47, 91, 255, 0.18)',
              transition: 'transform 120ms ease, box-shadow 120ms ease',
            }}
            onMouseEnter={(e) => (e.currentTarget.style.transform = 'translateY(-1px)')}
            onMouseLeave={(e) => (e.currentTarget.style.transform = 'translateY(0)')}
          >
            <span>Ask Curx</span>
            <span aria-hidden="true">→</span>
          </a>
          <p
            style={{
              margin: '10px 0 0',
              fontSize: 13,
              color: colors.textSecondary,
              fontFamily: fontStack,
            }}
          >
            What should I do today?
          </p>
        </div>
      </div>
    </SidebarLayout>
  );
}

function ModuleCard({ module }) {
  const { path, name, subtitle, metric, suggestion, accent, Icon } = module;
  return (
    <Card
      accentColor={accent}
      onClick={() => {
        window.location.href = path;
      }}
      style={{ display: 'flex', flexDirection: 'column', gap: 12 }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <span
          aria-hidden="true"
          style={{
            width: 32,
            height: 32,
            borderRadius: radius.sm,
            backgroundColor: hexWithAlpha(accent, 0.14),
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Icon color={accent} />
        </span>
        <div>
          <div
            style={{
              fontSize: 16,
              fontWeight: 600,
              color: colors.textPrimary,
              fontFamily: fontStack,
            }}
          >
            {name}
          </div>
          <div
            style={{
              fontSize: 12,
              color: colors.textSecondary,
              fontFamily: fontStack,
            }}
          >
            {subtitle}
          </div>
        </div>
      </div>
      {metric && (
        <div
          style={{
            fontSize: 22,
            fontWeight: 600,
            color: colors.textPrimary,
            fontFamily: fontStack,
            letterSpacing: '-0.01em',
          }}
        >
          {metric}
        </div>
      )}
      <div
        style={{
          fontSize: 13,
          color: colors.textSecondary,
          fontFamily: fontStack,
          lineHeight: 1.5,
          paddingTop: 8,
          borderTop: `1px solid ${colors.border}`,
        }}
      >
        <strong style={{ color: colors.textPrimary, fontWeight: 600 }}>
          Top suggestion:
        </strong>{' '}
        {suggestion}
      </div>
    </Card>
  );
}

function greeting() {
  const h = new Date().getHours();
  if (h < 5) return 'Up late';
  if (h < 12) return 'Good morning';
  if (h < 17) return 'Good afternoon';
  if (h < 22) return 'Good evening';
  return 'Up late';
}

function hexWithAlpha(hex, alpha) {
  // Accept #RRGGBB; return rgba string.
  const m = /^#([0-9a-f]{6})$/i.exec(hex);
  if (!m) return hex;
  const r = parseInt(m[1].slice(0, 2), 16);
  const g = parseInt(m[1].slice(2, 4), 16);
  const b = parseInt(m[1].slice(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

/* === Inline icons === */
function BoltIcon({ color }) {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none"
         stroke={color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <polygon points="13 2 4 14 11 14 10 22 20 10 13 10 13 2" />
    </svg>
  );
}
function SeedlingIcon({ color }) {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none"
         stroke={color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M12 21v-7" />
      <path d="M12 14c-4 0-7-3-7-7 4 0 7 3 7 7z" />
      <path d="M12 14c4 0 7-3 7-7-4 0-7 3-7 7z" />
    </svg>
  );
}
function GridIcon({ color }) {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none"
         stroke={color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <rect x="3" y="3" width="7" height="7" />
      <rect x="14" y="3" width="7" height="7" />
      <rect x="3" y="14" width="7" height="7" />
      <rect x="14" y="14" width="7" height="7" />
    </svg>
  );
}
